import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Pause, Play, RefreshCw } from 'lucide-react';
import type { AudioAsset, Language, Row } from '@tekvoice/shared';
import { useAudioPlayer } from '@/components/audio-player';
import { AudioStatusChip } from '@/components/status-chip';
import { useReview } from '@/components/review/review-context';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/sonner';
import { api } from '@/lib/api';
import { assetAudio, formatDuration } from '@/lib/audio';
import { canRegenerateAsset, effectiveStatus, isRegenerating, textFor } from '@/lib/assets';
import { cn } from '@/lib/utils';

interface AudioCellProps {
  row: Row;
  language: Language;
}

/**
 * SPEC §11 — play/pause, duration, status chip, error tooltip, regenerate.
 * Playback goes through the ONE global player, and the URL is only fetched on
 * first play.
 */
export function AudioCell({ row, language }: AudioCellProps): React.JSX.Element {
  const asset: AudioAsset | null = row.assets[language];
  const player = useAudioPlayer();
  const { applyAsset, openAb, canRegenerate, activeRunId } = useReview();

  const regenerate = useMutation({
    mutationFn: () => {
      if (!asset) throw new Error('No asset');
      return api.assets.regenerate(asset.id);
    },
    onSuccess: (result) => {
      applyAsset(row.id, result.asset);
      openAb({
        rowId: row.id,
        assetId: result.asset.id,
        language,
        newTakeId: result.newTake.id,
        newTakeUrl: result.newTake.url,
        previousTakeId: result.previousTake?.id ?? null,
        previousTakeUrl: result.previousTake?.url ?? null,
        suggestOverride: result.suggestOverride,
      });
    },
    onError: (error: Error) => toast.error(`Regenerate failed — ${error.message}`),
  });

  const text = textFor(row, language);
  if (!asset) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (text.trim().length === 0) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs italic text-muted-foreground">no text — skipped</span>
      </div>
    );
  }

  const status = effectiveStatus(asset) ?? asset.status;
  const audio = assetAudio(asset);
  const playerKey = `${asset.id}:${audio?.source ?? 'none'}:${asset.currentTakeId ?? 'x'}`;
  const playing = player.isPlaying(playerKey);
  const loading = player.isLoading(playerKey);
  const listened = player.hasListened(playerKey);

  const regenerating = isRegenerating(asset);
  const hint = regenerating
    ? 'A new take is being generated — the published clip is untouched until you choose one.'
    : status === 'FAILED'
      ? (asset.lastErrorMessage ?? 'Generation failed') +
        (asset.lastErrorCode ? ` (${asset.lastErrorCode})` : '')
      : status === 'STALE'
        ? 'Text changed — regenerate before approving'
        : status === 'GENERATING'
          ? 'Generating…'
          : null;

  return (
    <div className="flex flex-col gap-1.5" data-testid={`audio-cell-${language}-${row.rowIndex}`}>
      <div className="flex items-center gap-2">
        <Button
          size="icon"
          variant="outline"
          className="h-7 w-7"
          disabled={!audio}
          aria-label={
            playing ? `Pause ${language} audio` : `Play ${language} audio for row ${row.rowIndex + 1}`
          }
          onClick={() => {
            if (audio) player.toggle(playerKey, audio.url);
          }}
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : playing ? (
            <Pause className="h-3.5 w-3.5" />
          ) : (
            <Play className="h-3.5 w-3.5" />
          )}
        </Button>

        <span className="text-xs tabular-nums text-muted-foreground">
          {audio ? formatDuration(audio.durationMs) : '—'}
        </span>

        {listened ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={`listened-${language}-${row.rowIndex}`}
                aria-label="Listened all the way through"
                className="h-1.5 w-1.5 rounded-full bg-emerald-600"
              />
            </TooltipTrigger>
            <TooltipContent>Listened all the way through</TooltipContent>
          </Tooltip>
        ) : null}

        {canRegenerate ? (
          <Button
            size="icon"
            variant="ghost"
            className="ml-auto h-7 w-7"
            aria-label={`Regenerate ${language} audio for row ${row.rowIndex + 1}`}
            disabled={regenerate.isPending || !canRegenerateAsset(asset, activeRunId)}
            onClick={() => regenerate.mutate()}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', regenerate.isPending && 'animate-spin')} />
          </Button>
        ) : null}
      </div>

      <div className="flex items-center gap-1">
        <AudioStatusChip status={status} regenerating={regenerating} hint={hint} />
        {audio?.source === 'published' ? (
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">published</span>
        ) : null}
      </div>
    </div>
  );
}
